/**
 * Notifications — `/admin/notifications`.
 *
 * These are the signed-in user's own; the endpoint scopes them in SQL, not
 * only by permission, so there is no "whose" filter to offer and none is
 * implied.
 *
 * Two details the screen respects. `subject` and `body` are nullable: a
 * notification whose stored parameters no longer render against the current
 * template publishes null rather than a guess, and the screen says so instead
 * of printing an empty row. And `unread_count` is the user's real unread
 * total, independent of the page filter — so it stays truthful while the
 * "unread only" toggle is on.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCheck, Mail, MailOpen } from "lucide-react";
import { Badge, Button, Card, CardContent, Checkbox, Label } from "@erp/ui";

import { formatDateTime } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import { markAllNotificationsRead, markNotificationRead } from "../api";
import { platformKeys, useNotifications } from "../queries";

export interface NotificationsPageProps {
  canManage?: boolean;
}

export function NotificationsPage({ canManage = false }: NotificationsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const page = useNotifications(unreadOnly);
  const rows = page.data?.items ?? [];
  const unreadCount = page.data?.unread_count ?? 0;

  const when = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: platformKeys.notifications.all });

  const markOne = async (notificationId: string) => {
    setBusy(true);
    setError(null);
    try {
      await markNotificationRead(notificationId, newIdempotencyKey());
      await refresh();
    } catch {
      setError(t("platform.notifications.markError", "That notification could not be marked."));
    } finally {
      setBusy(false);
    }
  };

  const markAll = async () => {
    setBusy(true);
    setError(null);
    try {
      await markAllNotificationsRead(newIdempotencyKey());
      await refresh();
    } catch {
      setError(t("platform.notifications.markError", "That notification could not be marked."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            {t("platform.notifications.title", "Notifications")}
            {unreadCount > 0 ? <Badge>{unreadCount}</Badge> : null}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("platform.notifications.subtitle", "What the system has told you, newest first.")}
          </p>
        </div>
        {canManage && unreadCount > 0 ? (
          <Button type="button" variant="outline" disabled={busy} onClick={() => void markAll()}>
            <CheckCheck className="size-4" aria-hidden="true" />
            {t("platform.notifications.markAll", "Mark all read")}
          </Button>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="unread-only"
          checked={unreadOnly}
          onCheckedChange={(checked) => setUnreadOnly(checked === true)}
        />
        <Label htmlFor="unread-only">{t("platform.notifications.unreadOnly", "Unread only")}</Label>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          {page.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {unreadOnly
                ? t("platform.notifications.emptyUnread", "Nothing unread.")
                : t("platform.notifications.empty", "No notifications yet.")}
            </p>
          ) : (
            <ul className="divide-y">
              {rows.map((row) => (
                <li key={row.id} className="flex items-start gap-3 p-4">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 text-muted-foreground"
                    data-testid={row.read_at === null ? "unread" : "read"}
                  >
                    {row.read_at === null ? (
                      <Mail className="size-4" />
                    ) : (
                      <MailOpen className="size-4 opacity-50" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className={row.read_at === null ? "font-medium" : ""}>
                      {row.subject ??
                        t(
                          "platform.notifications.unrenderable",
                          "This message can no longer be rendered ({{code}}).",
                          { code: row.template_code },
                        )}
                    </p>
                    {row.body === null ? null : (
                      <p className="text-sm text-muted-foreground">{row.body}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t(`platform.notificationCategory.${row.category}`, row.category)} ·{" "}
                      {when(row.created_at)}
                    </p>
                  </div>
                  {canManage && row.read_at === null ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void markOne(row.id)}
                    >
                      {t("platform.notifications.markRead", "Mark read")}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
